/**
 * Runtime DOM transliteration for Tamil.
 *
 * When the user picks Tamil as their language, we walk every text node in
 * `document.body` and convert Latin-only strings to Tamil script. We keep
 * the original English text in a WeakMap so we can restore it when the
 * user switches back to English / Hindi.
 *
 * A MutationObserver re-applies the transliteration on every DOM change
 * (React re-renders, route navigation, modal open/close, etc.).
 *
 * Why DOM-level instead of a React JSX wrapper?
 *   React doesn't expose a child component's rendered output as `children`
 *   on its parent — so a JSX walker can't recurse past a custom component
 *   boundary. The DOM approach sees the final rendered HTML and reaches
 *   every text node regardless of how deeply nested it is.
 *
 * Risks handled:
 *   • Re-runs harmlessly on already-transliterated nodes (we mark them via
 *     a separate "alreadyTranslit" Set so we don't re-process every tick).
 *   • Skips form controls, code blocks, scripts, styles, contenteditable
 *     elements, and anything carrying `data-no-translit`.
 *   • Skips URLs / emails / numbers-only text.
 *   • When React updates a text node we re-transliterate it (the observer
 *     hears the `characterData` mutation and re-runs).
 */
import { transliterateToTamil } from './transliterate';
import { WORD_TA, WORD_TA_RX } from './tamilDict';

const TAMIL_RX = /[\u0B80-\u0BFF]/;
const LATIN_RX = /[A-Za-z]/;
const SKIP_TAGS = new Set([
  'INPUT', 'TEXTAREA', 'SELECT', 'OPTION', 'SCRIPT', 'STYLE',
  'CODE', 'PRE', 'KBD', 'SAMP',
]);

/**
 * Words we never transliterate even though they are plain Latin text —
 * mostly brand / product names that should always read the same way.
 * Matched case-insensitively as whole words.
 */
const BRAND_KEEP_WORDS = new Set([
  'beeweddy', 'beewedy',
  'whatsapp', 'instagram', 'facebook', 'youtube', 'google', 'razorpay',
  'msg91', 'meta', 'sms', 'otp', 'pdf', 'pwa', 'apk', 'url',
]);

const BRAND_KEEP_RX = new RegExp(
  '\\b(' + Array.from(BRAND_KEEP_WORDS).map((w) => w.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')).join('|') + ')\\b',
  'gi',
);

/** Maps text-node → original English so we can restore it later. */
const originalText = new WeakMap();
/** Tracks nodes we have already transliterated so we don't churn. */
const transliterated = new WeakSet();

const transliterateShielded = (text) => {
  // Two-stage shielding so curated phrases AND brand names both survive
  // the phonetic transliterator:
  //   1. Pull every hand-curated dictionary match out and replace it with
  //      a non-Latin sentinel that the transliterator can't touch.
  //   2. Pull every brand name (BeeWeddy, WhatsApp, …) out the same way.
  //   3. Run the phonetic transliterator on what's left.
  //   4. Restore sentinels back to the curated Tamil / original brand.
  const replacements = [];
  const reserve = (replacement) => {
    const idx = replacements.push(replacement) - 1;
    return `\u0001${idx}\u0001`;
  };

  let shielded = text.replace(WORD_TA_RX, (match) => {
    const taValue = WORD_TA[match.toLowerCase()];
    return reserve(taValue || match);
  });
  shielded = shielded.replace(BRAND_KEEP_RX, (m) => reserve(m));

  let out = transliterateToTamil(shielded);
  replacements.forEach((v, i) => {
    out = out.replace(`\u0001${i}\u0001`, v);
  });
  return out;
};

const shouldSkipNode = (textNode) => {
  const parent = textNode.parentElement;
  if (!parent) return true;
  if (SKIP_TAGS.has(parent.tagName)) return true;
  // contenteditable areas — user-edited content.
  if (parent.isContentEditable) return true;
  // Opt-out attribute / class for individual elements.
  if (parent.closest('[data-no-translit], .no-translit')) return true;
  return false;
};

const isTransliterable = (text) => {
  if (!text) return false;
  if (!LATIN_RX.test(text)) return false;
  if (TAMIL_RX.test(text)) return false;
  if (text.includes('://')) return false;
  if (/\S+@\S+\.\S+/.test(text)) return false;
  return true;
};

const transliterateNode = (textNode) => {
  if (textNode.nodeType !== Node.TEXT_NODE) return;
  if (shouldSkipNode(textNode)) return;

  // Capture original on first sight so we can restore later.
  const orig = originalText.get(textNode) ?? textNode.nodeValue;
  if (!isTransliterable(orig)) return;

  if (!originalText.has(textNode)) originalText.set(textNode, orig);

  const next = transliterateShielded(orig);
  if (next !== textNode.nodeValue) {
    textNode.nodeValue = next;
  }
  transliterated.add(textNode);
};

const restoreNode = (textNode) => {
  if (textNode.nodeType !== Node.TEXT_NODE) return;
  const orig = originalText.get(textNode);
  if (orig !== undefined && textNode.nodeValue !== orig) {
    textNode.nodeValue = orig;
  }
  transliterated.delete(textNode);
};

const walkText = (root, fn) => {
  if (!root) return;
  // ELEMENT_NODE (1), TEXT_NODE (3), DOCUMENT_FRAGMENT (11).
  if (root.nodeType === Node.TEXT_NODE) { fn(root); return; }
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  let n;
  while ((n = walker.nextNode())) fn(n);
};

let observer = null;
const handleMutations = (mutations) => {
  for (const m of mutations) {
    if (m.type === 'characterData') {
      // React rewrote a text node — re-apply transliteration to the new
      // value. Do NOT call originalText.set here — the original mapping
      // already exists, and we want to preserve the previous English.
      const node = m.target;
      if (node.nodeType !== Node.TEXT_NODE) continue;
      if (shouldSkipNode(node)) continue;
      if (TAMIL_RX.test(node.nodeValue || '')) continue; // already done
      if (!isTransliterable(node.nodeValue || '')) continue;
      // Update our stored original to the latest English value React rendered.
      originalText.set(node, node.nodeValue);
      node.nodeValue = transliterateShielded(node.nodeValue);
      transliterated.add(node);
      continue;
    }
    for (const added of m.addedNodes) {
      walkText(added, transliterateNode);
    }
  }
};

export const startTanglish = () => {
  if (observer) return; // already running
  walkText(document.body, transliterateNode);
  observer = new MutationObserver(handleMutations);
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
  });
};

export const stopTanglish = () => {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  walkText(document.body, restoreNode);
};
